import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import { productList } from 'indices-list'
import { atom } from 'jotai'
import moment from 'moment'
import { createPrices } from 'services/price-history'
import { assetConfig } from 'services/price-history/config'
import {
  ChainAsset,
  Portfolios,
  RawHistoricalIndexAsset,
  RawHistoricalIndexAssets,
} from 'services/price-history/interfaces'
import {
  getPriceRangesStore,
  getPricesStore,
  parsePortfolios,
  writePriceRangesStore,
  writePricesStore,
} from 'services/price-history/utils'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_DAILY_STATS } from 'services/query-v2/keys'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { fetchGraphQLData } from 'services/query/fetchers/data-graph'
import { getSubgraphNewUrl } from 'services/query/hooks'
import { Address, zeroAddress } from 'viem'
import { DailyStatistics } from './model'
import { getParamsForHistoricalIndexAssets } from './query-params'
import {
  HistoricalIndexAssetRefresh,
  getProductPortfolio,
  parsePortfoliosToStorage,
  parseStorageToPortfolios,
  writeProductPortfolio,
} from './utils'
import { z } from 'zod'

const ResponseSchema = z.object({
  indexes: z.array(
    z.object({
      id: z.string(),
      chainID: z.string(),
      symbol: z.string(),
      totalSupply: z.string(),
      history: z.array(
        z.object({
          assets: z.array(
            z.object({
              asset: z.string(),
              balance: z.string(),
              chainID: z.string(),
              timestamp: z.string(),
              weight: z.string().nullable(),
            }),
          ),
          timestamp: z.string(),
          totalSupply: z.string(),
        }),
      ),
    }),
  ),
})

type PriceEndpoint = {
  prices: string[]
  timestamps: number[]
}

export type PriceResponse = {
  [key: string]: PriceEndpoint
}

export const indexDailyStatsAddressAtom = atom<Address>(zeroAddress)
export const indexDailyStatsNetworkAtom = atom<NetworkType>(NetworkType.Mainnet)

const getHistoricalIndexAssets = (address: string) => ({
  url: getSubgraphNewUrl(address),
  params: getParamsForHistoricalIndexAssets(address),
})

export const historicalAssetsStatsQuery = async (
  key: string,
  network: NetworkType,
  address: Address,
  functionName: string,
) => {
  const productKey = `${network}:${address}`

  const cachedProductPortfolio = getProductPortfolio(productKey)
  const now = moment().unix()
  const { url: urlNew, params: paramsNew } = getHistoricalIndexAssets(address)
  let start = productList[address].startDate - 1
  let dailyHistoricalAssetsLeft: number = Number.MAX_VALUE
  if (cachedProductPortfolio !== undefined) {
    const parsedPortfolio = parseStorageToPortfolios(cachedProductPortfolio)

    //INFO: data in cache is fresh
    if (now - cachedProductPortfolio.lastUpdate <= 60) {
      // 1 min
      return parsedPortfolio
    }

    //INFO: data in cache need to be updated
    const result: Portfolios[] = parsedPortfolio
    const additional: RawHistoricalIndexAsset[] = []
    start = +cachedProductPortfolio.history[cachedProductPortfolio.history.length - 1].timestamp - 1
    while (dailyHistoricalAssetsLeft !== 0) {
      const res = await fetchGraphQLData<RawHistoricalIndexAssets>(urlNew, paramsNew(start))
      if (!res) {
        throw new Error(
          `Fails in historicalAssetsStatsQuery & ${functionName} functions with a key ${key} (updating cache)`,
        )
      }

      const response = ResponseSchema.parse(res)
      const lastHistoricalAssetsStats = response.indexes[0]
      if (
        lastHistoricalAssetsStats !== undefined &&
        lastHistoricalAssetsStats.history.length !== 0
      ) {
        additional.push(lastHistoricalAssetsStats)
        start = Number(
          lastHistoricalAssetsStats.history[lastHistoricalAssetsStats.history.length - 1].timestamp,
        )
      } else {
        start = now
      }
      dailyHistoricalAssetsLeft = lastHistoricalAssetsStats.history.length
    }

    if (dailyHistoricalAssetsLeft === 0) {
      const updatedPort: HistoricalIndexAssetRefresh = {
        ...cachedProductPortfolio,
        history: [
          ...cachedProductPortfolio.history.slice(0, -1),
          ...parsePortfoliosToStorage(additional).history,
        ],
      }

      writeProductPortfolio({ ...updatedPort }, productKey, now)
      result.push(...parsePortfolios(additional))
    }
    return result
  }

  //INFO: data in cache is empty need to be updated all the ranges
  const dailyHistoricalAssets: RawHistoricalIndexAsset[] = []
  while (dailyHistoricalAssetsLeft !== 0) {
    const res = await fetchGraphQLData<RawHistoricalIndexAssets>(urlNew, paramsNew(start))
    if (!res) {
      throw new Error(
        `Fails in historicalAssetsStatsQuery & ${functionName} functions with a key ${key} (empty cache)`,
      )
    }
    const response = ResponseSchema.parse(res)
    const lastHistoricalAssetsStats = response.indexes[0]

    if (lastHistoricalAssetsStats !== undefined && lastHistoricalAssetsStats.history.length !== 0) {
      dailyHistoricalAssets.push(lastHistoricalAssetsStats)
      start = Number(
        lastHistoricalAssetsStats.history[lastHistoricalAssetsStats.history.length - 1].timestamp,
      )
      dailyHistoricalAssetsLeft = lastHistoricalAssetsStats.history.length
    } else {
      dailyHistoricalAssetsLeft = 0
    }
  }
  if (dailyHistoricalAssets.length !== 0) {
    writeProductPortfolio(parsePortfoliosToStorage(dailyHistoricalAssets), productKey, now)
    const result = parsePortfolios(dailyHistoricalAssets)

    return result
  } else {
    return []
  }
}

const indexDailyStatsQuery = async <T>({
  queryKey: [key, address, network],
}: {
  queryKey: [string, Address, NetworkType]
}) => {
  const portfolio = await historicalAssetsStatsQuery(key, network, address, 'indexDailyStatsQuery')
  const { prices } = await createPrices(
    assetConfig,
    portfolio,
    getPricesStore,
    getPriceRangesStore,
    writePricesStore,
    writePriceRangesStore,
  )

  const currentPrices = getPricesStore()
  const productKey = `${network}:${address}` as ChainAsset
  const productPriceHistory = prices[productKey]
  const pricesEth = Object.values(currentPrices[`${network}:${zeroAddress}`]).slice(
    -productPriceHistory.length,
  )

  const parsedData = productPriceHistory.map((el, i) => {
    return {
      totalSupply: Big(0),
      basePriceETH: Big(pricesEth[i]),
      basePrice: el.value,
      date: new Date(el.timestamp * 1000),
    }
  })

  const result = {
    chainId: network,
    productAddress: address,
    currentPrice: parsedData[parsedData.length - 1].basePrice,
    priceHistory: parsedData,
  }
  return result as T
}

export const indexDailyStatFactory = () => {
  const indexAddressAtom = atom<Address>(zeroAddress)
  const indexNetworkAtom = atom<NetworkType>(NetworkType.Mainnet)

  const mainProperties = createAtomsWithQuery<
    DailyStatistics,
    Address,
    NetworkType,
    null,
    [string, Address, NetworkType]
  >(
    { first: indexAddressAtom, second: indexNetworkAtom },
    QUERY_KEY_DAILY_STATS,
    indexDailyStatsQuery<DailyStatistics>,
    DEFAULT_REFETCH_INTERVAL,
    'indexDailyStatsQuery',
  )

  return { chainId: indexNetworkAtom, productAddress: indexAddressAtom, ...mainProperties }
}
